<template>
    <div class="vr-page relative min-h-screen w-full">
        <div class="container mx-auto px-4 sm:px-8 py-8">

            <nav class="text-sm text-gray-500 mb-5 flex items-center flex-wrap">
                <router-link to="/dashboard" class="hover:text-blue-700 font-medium">Dashboard</router-link>
                <span class="mx-1.5 text-gray-300">/</span>
                <span class="text-gray-800 font-semibold">Volunteering &amp; Rewards Hub</span>
            </nav>

            <header class="mb-8">
                <p class="text-xs font-bold uppercase tracking-widest text-blue-600 m-0 mb-1">Engage+</p>
                <h1 class="text-3xl font-extrabold text-gray-900 m-0">Volunteering &amp; Rewards Hub</h1>
                <p class="text-gray-500 mt-1 mb-0 text-sm">Browse opportunities, track applications, and earn verified volunteering hours.</p>
            </header>

            <div v-if="statsLoading" class="flex justify-center py-10">
                <base-page-spinner type="spin" class-list="h-16"></base-page-spinner>
            </div>
            <div v-else class="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                <div class="vr-stat-card vr-stat-card--primary">
                    <div class="vr-stat-card__icon">
                        <font-awesome-icon :icon="['fas', 'clock']" />
                    </div>
                    <div class="vr-stat-card__body">
                        <p class="vr-stat-card__value">{{ formatHours(hoursSummary.total_hours) }}</p>
                        <p class="vr-stat-card__label">Total Hours</p>
                    </div>
                </div>
                <div class="vr-stat-card vr-stat-card--tier">
                    <div class="vr-stat-card__icon">
                        <font-awesome-icon :icon="['fas', 'award']" />
                    </div>
                    <div class="vr-stat-card__body">
                        <p class="vr-stat-card__value vr-stat-card__value--sm">{{ hoursSummary.reward_tier_name || 'Bronze' }}</p>
                        <p class="vr-stat-card__label">Reward Tier</p>
                        <p v-if="hoursSummary.next_tier_name" class="vr-stat-card__hint">
                            {{ formatHours(hoursSummary.hours_to_next_tier) }} to {{ hoursSummary.next_tier_name }}
                        </p>
                    </div>
                </div>
                <div class="vr-stat-card vr-stat-card--pending" @click="goToApplications('pending')">
                    <div class="vr-stat-card__icon">
                        <font-awesome-icon :icon="['fas', 'hourglass-half']" />
                    </div>
                    <div class="vr-stat-card__body">
                        <p class="vr-stat-card__value">{{ dashboardStats.pending }}</p>
                        <p class="vr-stat-card__label">Pending</p>
                    </div>
                </div>
                <div class="vr-stat-card vr-stat-card--approved" @click="goToApplications('approved')">
                    <div class="vr-stat-card__icon">
                        <font-awesome-icon :icon="['fas', 'check-circle']" />
                    </div>
                    <div class="vr-stat-card__body">
                        <p class="vr-stat-card__value">{{ dashboardStats.approved }}</p>
                        <p class="vr-stat-card__label">Approved</p>
                    </div>
                </div>
                <div class="vr-stat-card vr-stat-card--returned" @click="goToApplications('returned')">
                    <div class="vr-stat-card__icon">
                        <font-awesome-icon :icon="['fas', 'rotate-left']" />
                    </div>
                    <div class="vr-stat-card__body">
                        <p class="vr-stat-card__value">{{ dashboardStats.returned }}</p>
                        <p class="vr-stat-card__label">Returned</p>
                    </div>
                </div>
            </div>

            <div class="vr-tabs-bar mb-5">
                <button
                    v-for="tab in mainTabs"
                    :key="tab.value"
                    type="button"
                    class="vr-tab"
                    :class="{ 'vr-tab--active': activeTab === tab.value }"
                    @click="activeTab = tab.value"
                >
                    <font-awesome-icon :icon="tab.icon" class="vr-tab__icon" />
                    <span>{{ tab.label }}</span>
                    <span v-if="tab.badge" class="vr-tab__badge">{{ tab.badge }}</span>
                </button>
            </div>

            <!-- Browse -->
            <section v-if="activeTab === 'browse'" class="vr-panel">
                <div class="vr-panel__header">
                    <div>
                        <h2 class="vr-panel__title">Browse Opportunities</h2>
                        <p class="vr-panel__subtitle">Find volunteering events open for student applications.</p>
                    </div>
                    <div class="vr-filters">
                        <div class="vr-search-wrap">
                            <font-awesome-icon :icon="['fas', 'search']" class="vr-search-icon" />
                            <input
                                v-model="search"
                                type="text"
                                placeholder="Search opportunities..."
                                class="vr-input vr-search-input"
                                @keyup.enter="fetchBrowse"
                            />
                        </div>
                        <select v-model="browseFilter" class="vr-select" @change="onBrowseFilterChange">
                            <option value="open">Open for Applications</option>
                            <option value="upcoming">Upcoming</option>
                            <option value="completed">Completed</option>
                        </select>
                        <button type="button" class="vr-btn-primary" @click="fetchBrowse">Search</button>
                    </div>
                </div>

                <div v-if="loading" class="vr-panel__body flex justify-center py-12">
                    <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                </div>
                <div v-else-if="browseItems.length === 0" class="vr-empty">
                    <font-awesome-icon :icon="['fas', 'compass']" class="vr-empty__icon" />
                    <p class="vr-empty__title">No opportunities found</p>
                    <p class="vr-empty__text">Try a different filter or check back later for new volunteering events.</p>
                </div>
                <div v-else class="vr-panel__body">
                    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
                        <article v-for="item in browseItems" :key="item.token" class="vr-card">
                            <div class="vr-card-accent" aria-hidden="true"></div>
                            <div class="vr-card-main">
                                <div class="vr-card-top">
                                    <span v-if="item.viewer_has_applied" class="vr-chip vr-status-chip" :class="statusChipClass(item.viewer_application_status)">
                                        {{ formatStatus(item.viewer_application_status) }}
                                    </span>
                                    <span v-else-if="item.is_accepting_applications" class="vr-chip vr-status-chip vr-chip-open">Open</span>
                                    <span v-else class="vr-chip vr-status-chip vr-chip-closed">Closed</span>
                                </div>

                                <h3 class="vr-card-title" :title="item.title">{{ item.title }}</h3>

                                <div class="vr-desc-box">
                                    <p v-if="item.description" class="vr-card-desc">{{ truncate(item.description, 140) }}</p>
                                    <p v-else class="vr-card-desc vr-card-desc-muted">No description has been added for this opportunity yet.</p>
                                </div>

                                <div class="vr-info-grid">
                                    <div class="vr-info-block">
                                        <span class="vr-info-icon" aria-hidden="true">
                                            <font-awesome-icon :icon="['fas', 'calendar']" />
                                        </span>
                                        <span class="vr-info-content">
                                            <span class="vr-info-label">Date</span>
                                            <span class="vr-info-value">{{ formatFirstDate(item) }}</span>
                                        </span>
                                    </div>
                                    <div class="vr-info-block">
                                        <span class="vr-info-icon" aria-hidden="true">
                                            <font-awesome-icon :icon="['fas', 'clock']" />
                                        </span>
                                        <span class="vr-info-content">
                                            <span class="vr-info-label">Expected</span>
                                            <span class="vr-info-value">{{ formatHours(item.expected_hours) }}</span>
                                        </span>
                                    </div>
                                </div>

                                <div class="vr-card-context">
                                    <span class="vr-location-badge">
                                        <font-awesome-icon :icon="['fas', 'location-dot']" class="vr-location-badge__icon" />
                                        {{ item.location_name || 'Location TBD' }}
                                    </span>
                                </div>

                                <div class="vr-card-actions">
                                    <router-link
                                        v-if="item.viewer_has_applied"
                                        :to="applicationDetailPath(item)"
                                        class="vr-btn-outline"
                                    >
                                        View Application
                                    </router-link>
                                    <router-link
                                        v-else
                                        :to="'/volunteering-and-rewards-hub/' + item.token"
                                        class="vr-btn-outline"
                                    >
                                        View details
                                    </router-link>
                                    <button
                                        v-if="canApply(item)"
                                        type="button"
                                        class="vr-btn-primary"
                                        :disabled="applyingToken === item.token"
                                        @click="apply(item)"
                                    >
                                        {{ applyingToken === item.token ? 'Applying…' : 'Apply' }}
                                    </button>
                                </div>
                            </div>
                        </article>
                    </div>
                    <div v-if="browsePaginator" class="vr-panel__footer">
                        <pagination :paginator-info="browsePaginator" @page-changed="onBrowsePageChange" />
                    </div>
                </div>
            </section>

            <!-- Applications -->
            <section v-if="activeTab === 'applications'" class="vr-panel">
                <div class="vr-panel__header">
                    <div>
                        <h2 class="vr-panel__title">My Applications</h2>
                        <p class="vr-panel__subtitle">Track the status of your volunteering applications.</p>
                    </div>
                </div>

                <div class="vr-subtabs">
                    <button
                        v-for="tab in applicationTabs"
                        :key="tab.value"
                        type="button"
                        class="vr-subtab"
                        :class="{ 'vr-subtab--active': applicationStatus === tab.value }"
                        @click="selectApplicationStatus(tab.value)"
                    >
                        {{ tab.label }}
                    </button>
                </div>

                <div v-if="loading" class="vr-panel__body flex justify-center py-12">
                    <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                </div>
                <div v-else-if="applicationItems.length === 0" class="vr-empty">
                    <font-awesome-icon :icon="['fas', 'clipboard-list']" class="vr-empty__icon" />
                    <p class="vr-empty__title">No applications found</p>
                    <p class="vr-empty__text">Browse opportunities and apply to get started.</p>
                    <button type="button" class="vr-btn-primary mt-4" @click="activeTab = 'browse'">Browse Opportunities</button>
                </div>
                <div v-else class="vr-panel__body">
                    <div class="space-y-4">
                        <article v-for="app in applicationItems" :key="app.id" class="vr-app-card">
                            <div class="vr-app-card__main">
                                <div class="vr-app-card__icon">
                                    <font-awesome-icon :icon="['fas', 'hand-holding-heart']" />
                                </div>
                                <div class="vr-app-card__content">
                                    <h3 class="vr-app-card__title">{{ app.opportunity && app.opportunity.title }}</h3>
                                    <p class="vr-app-card__meta">Applied {{ formatDateTime(app.submitted_at) }}</p>
                                </div>
                                <span class="vr-badge" :class="'vr-badge--' + app.status">{{ formatStatus(app.status) }}</span>
                            </div>
                            <div class="vr-app-card__actions">
                                <router-link
                                    v-if="app.opportunity"
                                    :to="'/volunteering-and-rewards-hub/' + app.opportunity.token + '?application=' + app.id"
                                    class="vr-btn-outline vr-btn-sm"
                                >
                                    View Application
                                </router-link>
                                <button
                                    v-if="app.status === 'returned'"
                                    type="button"
                                    class="vr-btn-primary vr-btn-sm"
                                    @click="goToApplication(app)"
                                >
                                    Resubmit
                                </button>
                                <button
                                    v-if="canSubmitHours(app)"
                                    type="button"
                                    class="vr-btn-primary vr-btn-sm"
                                    @click="goToApplication(app)"
                                >
                                    Submit hours
                                </button>
                            </div>
                        </article>
                    </div>
                    <div v-if="applicationPaginator" class="vr-panel__footer">
                        <pagination :paginator-info="applicationPaginator" @page-changed="onApplicationPageChange" />
                    </div>
                </div>
            </section>

            <!-- Hours -->
            <section v-if="activeTab === 'hours'" class="vr-panel">
                <div class="vr-panel__header">
                    <div>
                        <h2 class="vr-panel__title">My Hours</h2>
                        <p class="vr-panel__subtitle">Verified volunteering hours credited to your profile.</p>
                    </div>
                </div>

                <div class="vr-hours-hero">
                    <div class="vr-hours-hero__copy">
                        <p class="vr-hours-hero__label">Total approved hours</p>
                        <p class="vr-hours-hero__value">{{ formatHours(hoursSummary.total_hours) }}</p>
                    </div>
                    <div v-if="hoursSummary.reward_tier_name" class="vr-hours-hero__tier">
                        <div class="vr-tier-badge">
                            <font-awesome-icon :icon="['fas', 'award']" />
                            <span>{{ hoursSummary.reward_tier_name }}</span>
                        </div>
                        <p v-if="hoursSummary.next_tier_name" class="vr-tier-next">
                            {{ formatHours(hoursSummary.hours_to_next_tier) }} until {{ hoursSummary.next_tier_name }}
                        </p>
                    </div>
                </div>

                <div v-if="!hoursSummary.recent_transactions || hoursSummary.recent_transactions.length === 0" class="vr-empty">
                    <font-awesome-icon :icon="['fas', 'clock']" class="vr-empty__icon" />
                    <p class="vr-empty__title">No credited hours yet</p>
                    <p class="vr-empty__text">Complete a volunteering opportunity and submit your hours for verification.</p>
                </div>
                <div v-else class="vr-panel__body">
                    <div class="vr-ledger">
                        <div v-for="tx in hoursSummary.recent_transactions" :key="tx.id" class="vr-ledger-row">
                            <div class="vr-ledger-row__icon">
                                <font-awesome-icon :icon="['fas', 'plus']" />
                            </div>
                            <div class="vr-ledger-row__content">
                                <p class="vr-ledger-row__title">{{ tx.description }}</p>
                                <p class="vr-ledger-row__date">{{ formatDateTime(tx.credited_at) }}</p>
                            </div>
                            <div class="vr-ledger-row__amount">+{{ formatHours(tx.hours) }}</div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import Pagination from '@/components/shared/pagination.vue';

const BROWSE_QUERY = gql`
  query VolunteeringBrowse($first: Int, $page: Int, $filter: String, $search: String) {
    volunteeringOpportunitiesForStudent(first: $first, page: $page, filter: $filter, search: $search) {
      error error_code message
      paginatorInfo { total currentPage lastPage perPage firstItem }
      data {
        token title description expected_hours location_name
        schedules { start_at end_at }
        is_accepting_applications viewer_has_applied viewer_application_id viewer_application_status
      }
    }
  }
`;

const APPLICATIONS_QUERY = gql`
  query MyVolunteeringApplications($first: Int, $page: Int, $status: String) {
    myVolunteeringApplications(first: $first, page: $page, status: $status) {
      error error_code message
      paginatorInfo { total currentPage lastPage perPage }
      data {
        id status submitted_at
        opportunity { token title has_ended }
        hour_submission { id status }
      }
    }
  }
`;

const STATS_QUERY = gql`
  query VolunteeringStudentStats {
    volunteeringStudentDashboardStats { error data { pending approved returned rejected } }
    myVolunteeringHoursSummary { error data { total_hours reward_tier_name reward_tier_slug next_tier_name hours_to_next_tier recent_transactions { id hours description credited_at } } }
  }
`;

const APPLY_MUTATION = gql`
  mutation ApplyVolunteering($token: String!) {
    applyVolunteeringOpportunity(token: $token) {
      error error_code message already_applied
    }
  }
`;

export default {
    name: 'VolunteeringUserView',
    components: { Pagination },
    data() {
        return {
            activeTab: 'browse',
            browseFilter: 'open',
            search: '',
            browseItems: [],
            browsePaginator: null,
            browsePage: 1,
            applicationItems: [],
            applicationPaginator: null,
            applicationPage: 1,
            applicationStatus: '',
            applicationTabs: [
                { value: '', label: 'All' },
                { value: 'pending', label: 'Pending' },
                { value: 'approved', label: 'Approved' },
                { value: 'returned', label: 'Returned' },
                { value: 'rejected', label: 'Rejected' },
            ],
            dashboardStats: { pending: 0, approved: 0, returned: 0, rejected: 0 },
            hoursSummary: { total_hours: 0, recent_transactions: [] },
            loading: false,
            statsLoading: false,
            applyingToken: null,
        };
    },
    computed: {
        mainTabs() {
            return [
                { value: 'browse', label: 'Browse', icon: ['fas', 'compass'], badge: '' },
                { value: 'applications', label: 'My Applications', icon: ['fas', 'clipboard-list'], badge: this.dashboardStats.pending || '' },
                { value: 'hours', label: 'My Hours', icon: ['fas', 'clock'], badge: '' },
            ];
        },
    },
    watch: {
        activeTab(val) {
            if (val === 'browse') this.fetchBrowse();
            if (val === 'applications') this.fetchApplications();
            if (val === 'hours') this.fetchStats();
        },
    },
    created() {
        this.fetchStats();
        this.fetchBrowse();
    },
    methods: {
        goToApplications(status) {
            this.activeTab = 'applications';
            this.applicationStatus = status || '';
            this.applicationPage = 1;
            this.fetchApplications();
        },
        onBrowseFilterChange() {
            this.browsePage = 1;
            this.fetchBrowse();
        },
        async fetchStats() {
            this.statsLoading = true;
            try {
                const { data } = await this.$apollo.query({ query: STATS_QUERY, fetchPolicy: 'network-only' });
                if (data.volunteeringStudentDashboardStats && data.volunteeringStudentDashboardStats.data) {
                    this.dashboardStats = data.volunteeringStudentDashboardStats.data;
                }
                if (data.myVolunteeringHoursSummary && data.myVolunteeringHoursSummary.data) {
                    this.hoursSummary = data.myVolunteeringHoursSummary.data;
                }
            } catch (e) {
                console.log(e);
            }
            this.statsLoading = false;
        },
        async fetchBrowse() {
            this.loading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: BROWSE_QUERY,
                    variables: { first: 9, page: this.browsePage, filter: this.browseFilter, search: this.search || null },
                    fetchPolicy: 'network-only',
                });
                const result = data.volunteeringOpportunitiesForStudent;
                if (result.error && result.error_code === 403) {
                    this.$router.replace('/no-permission');
                    return;
                }
                this.browseItems = result.data || [];
                this.browsePaginator = result.paginatorInfo;
            } catch (e) {
                console.log(e);
            }
            this.loading = false;
        },
        async fetchApplications() {
            this.loading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: APPLICATIONS_QUERY,
                    variables: { first: 10, page: this.applicationPage, status: this.applicationStatus || null },
                    fetchPolicy: 'network-only',
                });
                const result = data.myVolunteeringApplications;
                this.applicationItems = result.data || [];
                this.applicationPaginator = result.paginatorInfo;
            } catch (e) {
                console.log(e);
            }
            this.loading = false;
        },
        selectApplicationStatus(status) {
            this.applicationStatus = status;
            this.applicationPage = 1;
            this.fetchApplications();
        },
        onBrowsePageChange(page) {
            this.browsePage = page;
            this.fetchBrowse();
        },
        onApplicationPageChange(page) {
            this.applicationPage = page;
            this.fetchApplications();
        },
        canApply(item) {
            return item.is_accepting_applications && !item.viewer_has_applied && item.has_capacity !== false;
        },
        async apply(item) {
            this.applyingToken = item.token;
            try {
                const { data } = await this.$apollo.mutate({
                    mutation: APPLY_MUTATION,
                    variables: { token: item.token },
                });
                const result = data.applyVolunteeringOpportunity;
                if (result.error) {
                    this.showApplyToast('error', result.message || 'Failed to apply.');
                } else {
                    this.showApplyToast('success', result.message || 'Application submitted.');
                    this.fetchBrowse();
                    this.fetchStats();
                }
            } catch (e) {
                this.showApplyToast('error', (e && e.message) || 'Failed to apply.');
            }
            this.applyingToken = null;
        },
        showApplyToast(icon, message) {
            if (!this.$swal) return;
            var Toast = this.$swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
            });
            Toast.fire({
                icon: icon || 'success',
                title: message || 'Success',
            });
        },
        canSubmitHours(app) {
            if (app.status !== 'approved' || !app.opportunity) return false;
            if (app.hour_submission && app.hour_submission.status !== 'returned') return false;
            return app.opportunity.has_ended === true;
        },
        goToApplication(app) {
            if (app.opportunity) {
                var path = '/volunteering-and-rewards-hub/' + app.opportunity.token;
                if (app.id) {
                    path = path + '?application=' + app.id;
                }
                this.$router.push(path);
            }
        },
        applicationDetailPath(item) {
            if (!item || !item.token) return '/volunteering-and-rewards-hub';
            if (item.viewer_application_id) {
                return '/volunteering-and-rewards-hub/' + item.token + '?application=' + item.viewer_application_id;
            }
            return '/volunteering-and-rewards-hub/' + item.token;
        },
        formatStatus(s) {
            if (!s) return '';
            return s.charAt(0).toUpperCase() + s.slice(1);
        },
        statusChipClass(s) {
            return 'vr-chip-' + (s || 'open');
        },
        formatHours(h) {
            return parseFloat(h || 0).toFixed(1) + ' hrs';
        },
        formatDateTime(d) {
            if (!d) return '';
            return new Date(d).toLocaleString();
        },
        formatFirstDate(item) {
            if (!item.schedules || !item.schedules.length) return 'TBD';
            var start = new Date(item.schedules[0].start_at);
            if (isNaN(start.getTime())) return 'TBD';
            var pad = function (n) { return n < 10 ? '0' + n : String(n); };
            return pad(start.getDate()) + '/' + pad(start.getMonth() + 1) + '/' + start.getFullYear();
        },
        truncate(text, len) {
            if (!text) return '';
            return text.length > len ? text.substring(0, len) + '...' : text;
        },
    },
};
</script>

<style scoped lang="scss">
.vr-page {
    background: linear-gradient(135deg, #f3f4f6 0%, #eff6ff 100%);
}

.vr-stat-card {
    display: flex;
    align-items: center;
    gap: 0.875rem;
    padding: 1.25rem 1rem;
    border-radius: 1rem;
    background: #fff;
    border: 1px solid #e5e7eb;
    transition: box-shadow 0.2s, transform 0.2s;
}
.vr-stat-card--pending,
.vr-stat-card--approved,
.vr-stat-card--returned {
    cursor: pointer;
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px -6px rgba(0, 0, 0, 0.1);
    }
}
.vr-stat-card--primary {
    background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%);
    border: none;
    color: #fff;
    .vr-stat-card__label,
    .vr-stat-card__hint { color: rgba(255, 255, 255, 0.85); }
    .vr-stat-card__value { color: #fff; }
    .vr-stat-card__icon { background: rgba(255, 255, 255, 0.15); color: #fff; }
}
.vr-stat-card__icon {
    flex-shrink: 0;
    width: 2.75rem;
    height: 2.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.75rem;
    font-size: 1.125rem;
}
.vr-stat-card--tier .vr-stat-card__icon { background: #fef3c7; color: #d97706; }
.vr-stat-card--pending { border-left: 3px solid #d97706; }
.vr-stat-card--pending .vr-stat-card__icon { background: #fef3c7; color: #d97706; }
.vr-stat-card--approved { border-left: 3px solid #059669; }
.vr-stat-card--approved .vr-stat-card__icon { background: #d1fae5; color: #059669; }
.vr-stat-card--returned { border-left: 3px solid #ea580c; }
.vr-stat-card--returned .vr-stat-card__icon { background: #ffedd5; color: #ea580c; }
.vr-stat-card__value {
    font-size: 1.5rem;
    font-weight: 800;
    line-height: 1.2;
    margin: 0;
    color: #1e293b;
}
.vr-stat-card__value--sm { font-size: 1.125rem; }
.vr-stat-card__label {
    font-size: 0.8125rem;
    font-weight: 600;
    margin: 0.125rem 0 0;
    color: #475569;
}
.vr-stat-card__hint {
    font-size: 0.6875rem;
    margin: 0.25rem 0 0;
    color: #64748b;
}

.vr-tabs-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.375rem;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
}
.vr-tab {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    background: transparent;
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s;
    &:hover { background: #f1f5f9; color: #334155; }
}
.vr-tab--active {
    background: #2563eb;
    color: #fff;
    &:hover { background: #1d4ed8; color: #fff; }
}
.vr-tab__icon { font-size: 0.875rem; }
.vr-tab__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.25rem;
    height: 1.25rem;
    padding: 0 0.35rem;
    font-size: 0.6875rem;
    font-weight: 700;
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.25);
}
.vr-tab:not(.vr-tab--active) .vr-tab__badge {
    background: #fee2e2;
    color: #dc2626;
}

.vr-panel {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    overflow: hidden;
}
.vr-panel__header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.5rem 1.5rem 1rem;
    border-bottom: 1px solid #f1f5f9;
}
.vr-panel__title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
}
.vr-panel__subtitle {
    font-size: 0.8125rem;
    color: #6b7280;
    margin: 0.25rem 0 0;
}
.vr-panel__body { padding: 1.25rem 1.5rem 1.5rem; }
.vr-panel__footer {
    padding: 1rem 1.5rem;
    border-top: 1px solid #f1f5f9;
}

.vr-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
}
.vr-select,
.vr-input {
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
    color: #374151;
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    &:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
    }
}
.vr-search-wrap {
    position: relative;
    display: flex;
    align-items: center;
}
.vr-search-icon {
    position: absolute;
    left: 0.75rem;
    color: #9ca3af;
    font-size: 0.75rem;
    pointer-events: none;
}
.vr-search-input {
    padding-left: 2rem;
    min-width: 12rem;
}

.vr-btn-primary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    padding: 0.5rem 1rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    text-decoration: none;
    &:disabled { opacity: 0.6; cursor: not-allowed; }
}
.vr-btn-outline {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #2563eb;
    background: #fff;
    border: 1px solid #2563eb;
    border-radius: 0.5rem;
    cursor: pointer;
    text-decoration: none;
}
.vr-btn-sm {
    padding: 0.35rem 0.75rem;
    font-size: 0.75rem;
}

.vr-empty {
    padding: 4rem 1.5rem;
    text-align: center;
}
.vr-empty__icon {
    font-size: 2.5rem;
    color: #cbd5e1;
    margin-bottom: 1rem;
}
.vr-empty__title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 0.25rem;
}
.vr-empty__text {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
}

/* Opportunity cards */
.vr-card {
    position: relative;
    display: block;
    overflow: hidden;
    min-height: 360px;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    background: #ffffff;
    box-shadow: 0 2px 4px rgba(15, 23, 42, 0.03), 0 12px 30px rgba(15, 23, 42, 0.06);
    transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
    &:hover {
        transform: translateY(-4px);
        border-color: #bfdbfe;
        box-shadow: 0 8px 18px rgba(15, 23, 42, 0.07), 0 22px 42px rgba(37, 99, 235, 0.12);
    }
}
.vr-card-accent {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 4px;
    background: linear-gradient(90deg, #2563eb 0%, #06b6d4 100%);
}
.vr-card-main {
    height: 100%;
    padding: 24px 22px 20px;
    display: flex;
    flex-direction: column;
}
.vr-card-top {
    margin-bottom: 12px;
    display: flex;
    justify-content: flex-end;
}
.vr-chip {
    padding: 5px 10px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.045em;
    text-transform: uppercase;
    border-radius: 9999px;
    border: 1px solid transparent;
}
.vr-chip-open { border-color: #bfdbfe; color: #1d4ed8; background: #eff6ff; }
.vr-chip-closed { border-color: #e2e8f0; color: #64748b; background: #f8fafc; }
.vr-chip-pending { border-color: #fde68a; color: #92400e; background: #fef3c7; }
.vr-chip-approved { border-color: #a7f3d0; color: #065f46; background: #d1fae5; }
.vr-chip-rejected { border-color: #fecaca; color: #991b1b; background: #fee2e2; }
.vr-chip-returned { border-color: #fed7aa; color: #9a3412; background: #ffedd5; }
.vr-card-title {
    min-height: 50px;
    margin-bottom: 10px;
    color: #0f172a;
    font-size: 19px;
    line-height: 1.35;
    font-weight: 700;
}
.vr-desc-box {
    min-height: 60px;
    margin-bottom: 16px;
}
.vr-card-desc {
    color: #64748b;
    font-size: 13px;
    line-height: 1.65;
    margin: 0;
}
.vr-card-desc-muted { font-style: italic; }
.vr-info-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(95px, 0.55fr);
    gap: 10px;
    margin-bottom: 14px;
}
.vr-info-block {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    padding: 11px;
    border: 1px solid #e8eef7;
    border-radius: 12px;
    background: #f8fafc;
}
.vr-info-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border-radius: 9px;
    color: #2563eb;
    background: #eaf2ff;
    font-size: 0.8rem;
}
.vr-info-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}
.vr-info-label {
    color: #94a3b8;
    font-size: 9px;
    font-weight: 800;
    text-transform: uppercase;
}
.vr-info-value {
    color: #334155;
    font-size: 12px;
    font-weight: 700;
}
.vr-card-context {
    margin-bottom: 14px;
}
.vr-location-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 9px;
    border-radius: 8px;
    color: #475569;
    background: #f1f5f9;
    font-size: 11px;
    font-weight: 700;
}
.vr-location-badge__icon { font-size: 10px; color: #64748b; }
.vr-card-actions {
    display: flex;
    gap: 8px;
    margin-top: auto;
    padding-top: 16px;
    border-top: 1px solid #eef2f7;
}
.vr-card-actions .vr-btn-outline,
.vr-card-actions .vr-btn-primary {
    flex: 1 1 120px;
    min-height: 40px;
}

/* Applications */
.vr-subtabs {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #f1f5f9;
}
.vr-subtab {
    padding: 0.4rem 0.85rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 9999px;
    cursor: pointer;
}
.vr-subtab--active {
    background: #2563eb;
    border-color: #2563eb;
    color: #fff;
}
.vr-app-card {
    border: 1px solid #e5e7eb;
    border-radius: 0.875rem;
    padding: 1rem 1.25rem;
    background: #fafbfc;
}
.vr-app-card__main {
    display: flex;
    align-items: flex-start;
    gap: 0.875rem;
}
.vr-app-card__icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.75rem;
    background: #dbeafe;
    color: #2563eb;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.vr-app-card__content { flex: 1; min-width: 0; }
.vr-app-card__title {
    font-size: 0.9375rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
}
.vr-app-card__meta {
    font-size: 0.75rem;
    color: #6b7280;
    margin: 0.25rem 0 0;
}
.vr-app-card__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.875rem;
    padding-top: 0.875rem;
    border-top: 1px solid #e5e7eb;
}
.vr-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.5rem;
    border-radius: 9999px;
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    flex-shrink: 0;
}
.vr-badge--pending { background: #fef3c7; color: #92400e; }
.vr-badge--approved { background: #d1fae5; color: #065f46; }
.vr-badge--rejected { background: #fee2e2; color: #991b1b; }
.vr-badge--returned { background: #ffedd5; color: #9a3412; }

/* Hours */
.vr-hours-hero {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    margin: 0 1.5rem 1rem;
    padding: 1.5rem;
    border-radius: 0.875rem;
    background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%);
    color: #fff;
}
.vr-hours-hero__label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0;
    opacity: 0.85;
}
.vr-hours-hero__value {
    font-size: 2.25rem;
    font-weight: 800;
    margin: 0.25rem 0 0;
    line-height: 1.1;
}
.vr-tier-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.875rem;
    border-radius: 0.5rem;
    background: rgba(255, 255, 255, 0.15);
    font-weight: 700;
    font-size: 0.9375rem;
}
.vr-tier-next {
    font-size: 0.75rem;
    margin: 0.5rem 0 0;
    opacity: 0.9;
}
.vr-ledger { display: flex; flex-direction: column; gap: 0.75rem; }
.vr-ledger-row {
    display: flex;
    align-items: center;
    gap: 0.875rem;
    padding: 0.875rem 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
    background: #fafbfc;
}
.vr-ledger-row__icon {
    width: 2rem;
    height: 2rem;
    border-radius: 9999px;
    background: #d1fae5;
    color: #059669;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    flex-shrink: 0;
}
.vr-ledger-row__content { flex: 1; min-width: 0; }
.vr-ledger-row__title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
}
.vr-ledger-row__date {
    font-size: 0.75rem;
    color: #6b7280;
    margin: 0.15rem 0 0;
}
.vr-ledger-row__amount {
    font-size: 1rem;
    font-weight: 800;
    color: #059669;
    flex-shrink: 0;
}
</style>
